import React, { useContext } from "react";
import { Settings } from "@mui/icons-material";

import { MapContext } from "../MapProvider";

import { IButtonProps } from "./Toolbar";
import ToolbarToggleButton from "./ToolbarToggleButton";

interface GenericToggleButtonProps extends IButtonProps {
  /**
   * The draw mode value this button represents (e.g., "draw_path_preference_mode")
   */
  modeValue: string;
  /**
   * Explicitly control the selected state. When provided, overrides the default
   * mode-based selection.
   */
  isSelected?: boolean;
}

/**
 * A generic toggle button for switching between custom draw modes. This button shows a selected state when its mode is active and triggers a mode change when clicked.
 */
const GenericToggleButton: React.FC<GenericToggleButtonProps> = ({
  modeValue,
  icon,
  title = "Toggle mode",
  variant = "minimal",
  isSelected,
  mode,
  ...rest
}) => {
  const { map, drawControl } = useContext(MapContext);
  const selected = isSelected ?? mode === modeValue;

  const handleOnClick = () => {
    if (!drawControl.current) return;
    drawControl.current.changeMode(modeValue);
    map?.fire("draw.modechange", { type: "draw.modechange", mode: modeValue, userInitiated: true });
  };

  return (
    <ToolbarToggleButton
      title={title}
      {...rest}
      variant={variant}
      value={modeValue}
      selected={selected}
      onClick={handleOnClick}
      data-testid={`toolbar-toggle-${modeValue}`}
    >
      {icon || <Settings />}
    </ToolbarToggleButton>
  );
};

export default GenericToggleButton;
